// src/services/caseNoteService.js

import axiosInstance from "../config/axiosInstance";

const BASE_URL = "/caseNotes";

/**
 * Obtiene todas las notas asociadas a un caso específico, ordenadas por fecha.
 *
 * Backend:
 *   GET /caseNotes/by-case/{caseId}
 *
 * @param {string} caseId - Id del caso (Guid).
 * @param {boolean} includeInternal - Si se deben incluir notas internas
 * @returns {Promise<ApiResponse<CaseNoteDto[]>>}
 */
export const getCaseNotesByCaseId = async (caseId, includeInternal = false) => {
  const { data } = await axiosInstance.get(
    `${BASE_URL}/by-case/${caseId}?includeInternal=${includeInternal}`
  );
  return data;
};

/**
 * Obtiene el detalle de una nota de caso por su Id.
 *
 * Backend:
 *   GET /caseNotes/{id}
 *
 * @param {string} id - Id de la nota (Guid).
 * @returns {Promise<ApiResponse<CaseNoteDto>>}
 */
export const getCaseNoteById = async (id) => {
  const { data } = await axiosInstance.get(`${BASE_URL}/${id}`);
  return data;
};

/**
 * Crea una nueva nota de caso.
 *
 * Backend:
 *   POST /caseNotes
 *
 * Espera un payload de tipo CaseNoteCreateDto:
 * {
 *   caseId: string (Guid),
 *   title: string,
 *   body: string,
 *   isInternal: boolean,
 *   attachments: [
 *     {
 *       attachmentTypeId: string (Guid),
 *       storedFileId: string (Guid),
 *       notes?: string
 *     }
 *   ]
 * }
 *
 * @param {CaseNoteCreateDto} payload - Datos de la nota a crear.
 * @returns {Promise<ApiResponse<string | null>>} Id de la nota creada (Guid).
 */
export const createCaseNote = async (payload) => {
  const { data } = await axiosInstance.post(BASE_URL, payload);
  return data;
};

/**
 * Crea una nueva nota de caso para un CaseId específico enviado en la ruta.
 *
 * Backend:
 *   POST /caseNotes/case/{caseId}
 *
 * Si el body (CaseNoteCreateDto) no trae CaseId, el backend lo fuerza con el de la ruta.
 *
 * @param {string} caseId - Id del caso (Guid).
 * @param {CaseNoteCreateDto} payload - Datos de la nota a crear.
 * @returns {Promise<ApiResponse<string | null>>} Id de la nota creada (Guid).
 */
export const createCaseNoteForCase = async (caseId, payload) => {
  const { data } = await axiosInstance.post(
    `${BASE_URL}/case/${caseId}`,
    payload
  );
  return data;
};

/**
 * Elimina una nota de caso.
 *
 * Backend:
 *   DELETE /caseNotes/{id}
 *
 * @param {string} id - Id de la nota (Guid).
 * @returns {Promise<ApiResponse<boolean>>}
 */
export const deleteCaseNote = async (id) => {
  const { data } = await axiosInstance.delete(`${BASE_URL}/${id}`);
  return data;
};

/**
 * Elimina un adjunto específico de una nota de caso.
 * NO elimina la nota, solo el adjunto.
 *
 * Backend:
 *   DELETE /caseNotes/attachments/{id}
 *
 * @param {string} attachmentId - Id del adjunto (CaseNoteAttachment.Id).
 * @returns {Promise<ApiResponse<boolean>>}
 */
export const deleteCaseNoteAttachment = async (attachmentId) => {
  try {
    const { data } = await axiosInstance.delete(
      `${BASE_URL}/attachments/${attachmentId}`
    );
    return data;
  } catch (error) {
    console.error("Error al eliminar el adjunto de la nota de caso:", error);
    throw error;
  }
};
